import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { usePhoneCall, AddCallerIdResponse } from "@/state/hooks/usePhoneCall";
import {
    PHONE_COUNTRY_CODES,
    sanitizeLocalPhoneInput,
    isValidPhoneNumber,
    extractDigits,
    parsePhoneForEdit,
    PhoneCountryCode,
} from "@/common/utils/phone";
import { getPhoneApiErrorMessage } from "@/common/utils/phoneApiErrors";
import PhoneNumberFormFields from "@/features/phone-call-widget/PhoneNumberFormFields";
import { dialSequenceProblem, type DialStep } from "@/features/phone-call-widget/dialSequence";
import { callerIdDialPayload, callerIdDialSteps } from "@/features/phone-call-widget/callerIdExtension";
import PlusCircleIcon from "@icons/plus-circle.svg?react";

interface CallerIdNumberProps {
    onCancel: () => void;
    onSave: (result: AddCallerIdResponse) => void;
    initialPhoneNumber?: string;
    initialReferenceName?: string;
    initialExtension?: string;
    /**
     * EN-1050 Task 8: structured dial sequence, preferred over `initialExtension` when present
     * (non-null) — the structured source of truth from a previous verification attempt. Falls
     * back to `parse(initialExtension ?? "")` when absent, which is the case for a first attempt
     * or a legacy row written before this feature.
     */
    initialDialSteps?: DialStep[] | null;
    /**
     * True when retrying verification on an existing row. The backend resolves the retry onto that
     * row by exact number match, so an edited number would insert a second row and orphan the one
     * being retried — the number is therefore locked, matching CallerIdVerificationModal.
     */
    isRetry?: boolean;
}

/**
 * CallerIdNumberForm component for entering a caller ID phone number
 * Allows user to enter phone number with country code and reference name
 */
const CallerIdNumberForm = ({
    onCancel,
    onSave,
    initialPhoneNumber,
    initialReferenceName,
    initialExtension,
    initialDialSteps,
    isRetry = false,
}: CallerIdNumberProps) => {
    const initialValues = parsePhoneForEdit(initialPhoneNumber ?? "");
    const initialCountryCodeKey =
        Object.entries(PHONE_COUNTRY_CODES).find(([, v]) => v === initialValues.countryCode)?.[0] ?? "US";
    const [countryCodeKey, setCountryCodeKey] = useState<string>(initialCountryCodeKey);
    const [phoneNumber, setPhoneNumber] = useState<string>(initialValues.localFormatted);
    const [referenceName, setReferenceName] = useState<string>(initialReferenceName ?? "");
    // dialSteps is the structured source of truth (preferred over parse(initialExtension) — see the
    // doc comment on `initialDialSteps` above). Unlike CallerIdVerificationModal, this component
    // doesn't unmount/remount across retries, so the seed is computed once here rather than in a
    // reset effect. `callerIdDialSteps` is the one shared derivation — see callerIdExtension.ts.
    const [dialSteps, setDialSteps] = useState<DialStep[]>(() =>
        callerIdDialSteps({ dialSteps: initialDialSteps, extension: initialExtension }),
    );
    const numberInputRef = useRef<HTMLInputElement>(null);
    const { addCallerIdMutation } = usePhoneCall();
    const dialCode = PHONE_COUNTRY_CODES[countryCodeKey as PhoneCountryCode] ?? PHONE_COUNTRY_CODES.US;

    // Focus the number input when the caller ID number is opened. Skipped on retry: the input is
    // disabled then, so focus() is a no-op that would leave focus on the body rather than landing
    // somewhere useful.
    useEffect(() => {
        if (!isRetry && numberInputRef.current) {
            numberInputRef.current.focus();
        }
    }, []);

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(sanitizeLocalPhoneInput(e.target.value, dialCode));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!phoneNumber || !isValidPhoneNumber(phoneNumber, dialCode)) {
            return;
        }

        try {
            // extension is derived from dialSteps — see the identical comment in
            // CallerIdVerificationModal.handleAddSubmit for why isExtensionValid is not the right
            // validator here, and `dialSequenceProblem` in the isValid gate below for what is.
            const result = await addCallerIdMutation.mutateAsync({
                phoneNumber: extractDigits(phoneNumber),
                countryCode: dialCode,
                referenceName: referenceName.trim() || "Phone Number",
                // Shared with CallerIdVerificationModal — see callerIdDialPayload in
                // callerIdExtension.ts for why a wait-only sequence omits `extension`.
                ...callerIdDialPayload(dialSteps),
            });
            onSave(result);
        } catch (error: unknown) {
            const message = await getPhoneApiErrorMessage(error);
            toast.error(message);
        }
    };

    // The builder can express sequences the server rejects (over 20 steps,
    // over 32 characters in a press, a legacy wait over 120s) and sequences that could never verify
    // (longer than the call waits for DTMF). `dialSequenceProblem` is the single gate, and
    // `DialSequenceField` renders the same string it returns — so Save is never disabled without
    // the reason on screen, which §7 of the design requires.
    const isValid =
        isValidPhoneNumber(phoneNumber, dialCode) &&
        referenceName.trim().length > 0 &&
        dialSequenceProblem(dialSteps) === null;

    return (
        <form onSubmit={handleSubmit} className="w-full h-full flex flex-col bg-shadow-950 rounded-lg shadow">
            {/* Header - shadow-850 #232C33 to match Dialer */}
            <div className="px-6 py-5 flex flex-col gap-1 bg-shadow-850">
                <h3 className="text-white text-lg font-semibold">Caller ID Number</h3>
                <p className="text-white text-sm">
                    Enter the number you want clients to see. We recommend your practice's main line. You'll need to
                    verify it's active.
                </p>
            </div>

            {/* `min-h-0` is load-bearing, not defensive. This body is a flex item in a fixed
                `h-[500px] overflow-hidden` frame (PhoneCallWidget); a flex item's default
                `min-height:auto` pins it to its content height, so once the EN-1050 step builder
                pushed the content past 500px the frame clipped the Save & Verify footer out of the
                widget entirely. `min-h-0` lets the item shrink to its flex share so the overflow
                scrolls here instead of being cut off, which keeps the footer on screen. */}
            <div className="flex-1 min-h-0 overflow-y-auto bg-shadow-950 px-6 py-5 flex flex-col gap-4">
                <PhoneNumberFormFields
                    variant="dark"
                    idPrefix="caller-id-widget"
                    isCallerIdSurface
                    countryCodeKey={countryCodeKey}
                    onCountryCodeChange={setCountryCodeKey}
                    phoneNumber={phoneNumber}
                    onPhoneNumberChange={handlePhoneNumberChange}
                    phoneInputRef={numberInputRef}
                    disabled={isRetry}
                    dialSteps={dialSteps}
                    onDialStepsChange={setDialSteps}
                    referenceName={referenceName}
                    onReferenceNameChange={setReferenceName}
                    referenceNamePlaceholder="Practice Number"
                />
            </div>

            {/* Footer buttons */}
            <div className="px-6 py-5 bg-shadow-800 flex gap-3 rounded-b-lg">
                <button type="button" onClick={onCancel} className="secondary-button flex-1 !text-max-800">
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!isValid || addCallerIdMutation.isPending}
                    className="primary-button flex-1 flex items-center justify-center gap-2"
                >
                    <PlusCircleIcon className="h-4 w-4" aria-hidden />
                    {addCallerIdMutation.isPending ? "Saving..." : "Save & Verify"}
                </button>
            </div>
        </form>
    );
};

export default CallerIdNumberForm;
